import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ExternalLink, MapPin, Phone, Copy } from "lucide-react";
import EditableNote from "@/components/EditableNote";

const copiar = (e, g) => { e.stopPropagation(); navigator.clipboard?.writeText(g).then(() => toast.success(`Guía ${g} copiada`)).catch(() => toast.error("No se pudo copiar")); };

export const GuiasPedido = ({ r, big = false }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={`seg-guias-${r.pedido}`}>
    {(r.guias_pedido || [r.guia]).map((g) => (
      <span key={g} role="button" tabIndex={0} onClick={(e) => copiar(e, g)} onKeyDown={(e) => e.key === "Enter" && copiar(e, g)}
        className={`inline-flex items-center gap-1 rounded border-2 font-mono font-bold cursor-pointer ${g === r.guia ? "border-zinc-900 bg-zinc-100" : "border-zinc-300 bg-white"} ${big ? "text-lg px-3 py-2" : "text-sm px-2 py-1"}`}
        title="Copiar guía" data-testid={`seg-copiar-${g}`}>
        {g} <Copy className={big ? "w-5 h-5" : "w-3.5 h-3.5"} />
      </span>
    ))}
  </div>
);

export const SEG_COLOR = { en_ruta: "bg-blue-600 text-white", entregado: "bg-emerald-600 text-white" };
const SEG_ROJO = "bg-red-600 text-white";
export const SegPill = ({ r }) => <span className={`text-xs font-black px-2 py-1 rounded uppercase ${SEG_COLOR[r.estado] || SEG_ROJO}`}>{r.estado_lbl}</span>;

/** Total de guías del pedido y estado de cada una en letras grandes (rojo = distinto de En ruta / Entregado). */
export const GuiasEstado = ({ r, big = false }) => {
  const gs = r.guias_estado || [];
  if (!gs.length) return null;
  const cnt = {};
  gs.forEach((g) => { cnt[g.estado] = cnt[g.estado] || { lbl: g.estado_lbl, n: 0 }; cnt[g.estado].n += 1; });
  return (
    <div className="space-y-1.5" data-testid={`seg-guias-estado-${r.pedido}`}>
      <div className={`${big ? "text-2xl" : "text-lg"} font-black leading-tight`} data-testid={`seg-guias-total-${r.pedido}`}>
        {gs.length} guía{gs.length === 1 ? "" : "s"}:{" "}
        {Object.entries(cnt).map(([k, v], i) => (
          <span key={k} className={(SEG_COLOR[k] ? "" : "text-red-600") + (k === "en_ruta" ? " text-blue-700" : k === "entregado" ? " text-emerald-700" : "")}>{i ? ", " : ""}{v.n} {v.lbl.toLowerCase()}</span>
        ))}
      </div>
      {big && gs.map((g) => (
        <div key={g.guia} className="flex items-center justify-between gap-2 text-lg" data-testid={`seg-guia-estado-${g.guia}`}>
          <span className="font-mono font-black break-all">{g.guia}</span>
          <span className={`text-base font-black px-3 py-1 rounded uppercase shrink-0 ${SEG_COLOR[g.estado] || SEG_ROJO}`}>{g.estado_lbl}</span>
        </div>
      ))}
    </div>
  );
};
const LiqTxt = ({ l }) => !l?.estado ? null : <span className={`text-xs font-black px-2 py-1 rounded uppercase ${l.estado === "liquidado" ? "bg-emerald-600 text-white" : "bg-orange-500 text-white"}`} data-testid="seg-liq-estado">{l.estado === "liquidado" ? `Liquidado #${l.numero} · ${l.fecha}` : "En proceso de liquidación"}</span>;

export const SeguimientoCard = ({ r, onOpen }) => (
  <button onClick={onOpen} className={`w-full text-left bg-white border-2 ${r.prioridad ? "border-red-600" : "border-zinc-200"} rounded-lg p-4 shadow-sm space-y-2`} data-testid={`seg-card-${r.guia}`}>
    <div className="flex items-center justify-end gap-2"><SegPill r={r} /></div>
    <div className="text-lg font-black">{r.pedido} · <span className="font-bold">{r.cliente}</span></div>
    <GuiasEstado r={r} />
    <GuiasPedido r={r} />
    {r.destino && <div className="text-sm text-zinc-500 flex gap-1"><MapPin className="w-4 h-4" />{r.destino}</div>}
    <div className="text-sm">{r.transportista} · colocado {r.colocado_at}</div>
    <div className="text-xs text-zinc-500">Estado desde {r.estado_at}</div>
    <LiqTxt l={r.liquidacion} />
    {r.fotos.length > 0 && <div className="flex gap-1 overflow-x-auto">{r.fotos.slice(0, 4).map((f) => <img key={f} src={f} alt="empaque" className="w-14 h-14 object-cover rounded border" />)}</div>}
  </button>
);

const CambiarEstado = ({ r, estados, onDone }) => {
  const [estado, setEstado] = useState(r.estado);
  const [obs, setObs] = useState("");
  const [fp, setFp] = useState("");
  const [busy, setBusy] = useState(false);
  const [todo, setTodo] = useState(false);
  const varias = (r.guias_pedido || []).length > 1;
  const go = async () => {
    if (estado === "entregado" && !window.confirm(todo ? "¿Confirmar que el cliente RECIBIÓ TODAS las guías del pedido?" : "¿Confirmar que el cliente RECIBIÓ el paquete?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/seguimiento-app/paquete/${encodeURIComponent(r.guia)}/estado`, { estado, observaciones: obs, fecha_programada: fp, todo_pedido: todo });
      toast.success(`${todo ? `Pedido ${r.pedido} (${data.guias.length} guías)` : `Guía ${r.guia}`}: ${data.estado_lbl}${data.pedido_entregado ? " · pedido ENTREGADO, en proceso de liquidación" : ""}`);
      setObs(""); onDone(estado);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border-2 border-[#002FA7] p-4 space-y-2" data-testid="seg-cambiar">
      <div className="text-xs uppercase font-black text-[#002FA7]">Actualizar estado (después de verificar en el portal)</div>
      <a href="https://www.cargoexpreso.com/" target="_blank" rel="noreferrer" onClick={() => navigator.clipboard?.writeText(r.guia)} className="w-full h-12 rounded-lg border-2 border-zinc-300 font-bold flex items-center justify-center gap-2" data-testid="seg-portal"><ExternalLink className="w-5 h-5" /> Consultar en Cargo Expreso (guía copiada)</a>
      <select value={estado} onChange={(e) => setEstado(e.target.value)} className="w-full h-14 rounded-lg border-2 border-zinc-300 px-3 text-lg font-bold" data-testid="seg-estado-select">
        {Object.entries(estados).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      {estado === "reprogramado" && <input type="date" value={fp} onChange={(e) => setFp(e.target.value)} className="w-full h-12 rounded-lg border-2 border-zinc-300 px-3" data-testid="seg-fecha-prog" />}
      <textarea value={obs} onChange={(e) => setObs(e.target.value)} rows={2} placeholder="Observaciones" className="w-full rounded-lg border-2 border-zinc-300 p-3 text-base" data-testid="seg-obs" />
      {varias && (
        <div className="grid grid-cols-2 gap-2" data-testid="seg-alcance">
          <button type="button" onClick={() => setTodo(false)} className={`h-12 rounded-lg border-2 text-sm font-black ${!todo ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-zinc-300"}`} data-testid="seg-alcance-guia">Solo guía {r.guia}</button>
          <button type="button" onClick={() => setTodo(true)} className={`h-12 rounded-lg border-2 text-sm font-black ${todo ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-zinc-300"}`} data-testid="seg-alcance-pedido">Todo el pedido ({r.guias_pedido.length})</button>
        </div>
      )}
      <button onClick={go} disabled={busy} className="w-full h-14 rounded-lg bg-[#002FA7] text-white text-lg font-black uppercase disabled:opacity-50" data-testid="seg-guardar-estado">Guardar estado</button>
    </div>
  );
};

export const Liquidar = ({ orderId, onDone }) => {
  const { user } = useAuth();
  const [numero, setNumero] = useState("");
  const [fecha, setFecha] = useState(new Date().toLocaleDateString("en-CA", { timeZone: "America/Guatemala" }));
  const [dup, setDup] = useState(false);
  const go = async () => {
    try { await api.post(`/orders/${orderId}/liquidacion`, { numero, fecha, autorizar_duplicado: dup }); toast.success("Pedido LIQUIDADO"); onDone(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-2" data-testid={`seg-liquidar-${orderId}`}>
      <input value={numero} onChange={(e) => setNumero(e.target.value)} placeholder="Número de liquidación" className="w-full h-12 rounded-lg border-2 border-zinc-300 px-3 text-lg" data-testid="seg-liq-numero" />
      <input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} className="w-full h-12 rounded-lg border-2 border-zinc-300 px-3" data-testid="seg-liq-fecha" />
      {user?.role === "admin" && <label className="flex gap-2 text-sm"><input type="checkbox" checked={dup} onChange={(e) => setDup(e.target.checked)} /> Autorizar número repetido (corrección)</label>}
      <button onClick={go} disabled={!numero.trim() || !fecha} className="w-full h-14 rounded-lg bg-emerald-600 text-white text-lg font-black uppercase disabled:opacity-40" data-testid="seg-liq-guardar">Confirmar liquidado</button>
    </div>
  );
};

export const SeguimientoDetalle = ({ r, estados, onReload }) => (
  <div className="space-y-4" data-testid="seg-detalle">
    <div className={`rounded-lg border-2 p-4 ${r.prioridad ? "border-red-600" : "border-zinc-200"}`}>
      <div className="flex justify-end items-center"><SegPill r={r} /></div>
      <div className="text-2xl font-black mt-1">{r.pedido}</div>
      <div className="rounded-lg border-2 border-zinc-900 p-3 my-2"><GuiasEstado r={r} big /></div>
      <div className="text-xs uppercase font-black text-zinc-500 mt-2 mb-1">Guías del pedido (toque para copiar)</div>
      <GuiasPedido r={r} big />
      <div className="text-xl font-bold">{r.cliente}</div>
      {r.telefono && <div className="flex gap-2 items-center mt-1"><Phone className="w-5 h-5 text-emerald-700" /><b>{r.telefono}</b></div>}
      <div className="text-base mt-1">{r.transportista} · colocado {r.colocado_at}</div>
      <div className="text-base">A cobrar: <b>Q{r.a_cobrar.toFixed(2)}</b> · Entrega pedido: <b>{(r.estado_entrega || "pendiente").replace(/_/g, " ")}</b></div>
    </div>
    {r.estado !== "entregado" && <CambiarEstado r={r} estados={estados} onDone={onReload} />}
    {r.estado_entrega === "entregado" && (
      <div className="rounded-lg border-2 border-orange-400 p-4 space-y-2" data-testid="seg-liquidacion">
        <div className="text-xs uppercase font-black text-orange-700">Liquidación</div>
        <LiqTxt l={r.liquidacion} />
        {r.liquidacion.estado !== "liquidado" && <Liquidar orderId={r.order_id} onDone={onReload} />}
        {r.liquidacion.historial.map((h, i) => <div key={i} className="text-xs text-zinc-600">{h.fecha_hora} · {h.by} · {h.estado}{h.numero ? ` #${h.numero}` : ""}</div>)}
      </div>
    )}
    <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4"><div className="text-xs uppercase font-black text-amber-800 mb-1">Notas de entrega</div><EditableNote orderId={r.order_id} modulo="notas_ent" initial={r.notas_ent} onSaved={onReload} /></div>
    <div className="rounded-lg border-2 border-zinc-200 p-4" data-testid="seg-historial">
      <div className="text-xs uppercase font-black text-zinc-500 mb-2">Historial</div>
      {r.eventos.map((e, i) => <div key={i} className="text-sm border-t border-zinc-100 py-1">{e.fecha} · <b>{e.estado}</b> · {e.by}{e.obs ? ` · ${e.obs}` : ""}</div>)}
    </div>
    {r.manifiesto && <div className="rounded-lg border-2 border-zinc-200 p-4"><div className="text-xs uppercase font-black text-zinc-500 mb-2">Manifiesto {r.manifiesto_agencia}</div><a href={r.manifiesto} target="_blank" rel="noreferrer"><img src={r.manifiesto} alt="manifiesto" className="max-h-60 rounded border" /></a></div>}
    <div className="rounded-lg border-2 border-zinc-200 p-4"><div className="text-xs uppercase font-black text-zinc-500 mb-2">Fotos de empaque ({r.fotos.length})</div><div className="grid grid-cols-3 gap-2">{r.fotos.map((f) => <a key={f} href={f} target="_blank" rel="noreferrer"><img src={f} alt="empaque" className="w-full aspect-square object-cover rounded border" /></a>)}</div></div>
  </div>
);
